import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useGameStore } from "../../store/gameStore";
import { api } from "../../lib/api";
import "./Shop.css";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ShopItem {
  id: string;
  name: string;
  price: number;
  type: "avatar_accessory" | "boost" | "consumable";
  description: string;
}

interface InventoryEntry {
  resource_id: string;
  quantity: number;
}

const ITEM_EMOJI: Record<string, string> = {
  hat_1:       "🎩",
  boost_gold:  "⚡",
  hint_token:  "💡",
};

const TYPE_LABEL: Record<string, string> = {
  avatar_accessory: "Aksessoar",
  boost:            "Styrke",
  consumable:       "Engongsbruk",
};

// ─── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ msg, error }: { msg: string; error?: boolean }) {
  return <div className={`toast${error ? " toast--error" : ""}`}>{msg}</div>;
}

// ─── ShopScreen ────────────────────────────────────────────────────────────────

export function ShopScreen() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const gold = useGameStore((s) => s.playerGold);
  const setGold = useGameStore((s) => s.setGold);

  const [items, setItems] = useState<ShopItem[]>([]);
  const [inventory, setInventory] = useState<Record<string, number>>({});
  const [buying, setBuying] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; error?: boolean } | null>(null);

  // Fetch catalog + inventory in parallel
  useEffect(() => {
    let cancelled = false;

    async function load() {
      const [shopRes, invRes] = await Promise.all([
        api.get<{ items: ShopItem[] }>("/shop"),
        token ? api.get<{ inventory: InventoryEntry[] }>("/inventory", token) : Promise.resolve(null),
      ]);

      if (cancelled) return;

      if (shopRes.ok) setItems(shopRes.data.items);

      if (invRes && invRes.ok) {
        const map: Record<string, number> = {};
        for (const e of invRes.data.inventory) map[e.resource_id] = e.quantity;
        setInventory(map);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [token]);

  function showToast(msg: string, error = false) {
    setToast({ msg, error });
    setTimeout(() => setToast(null), 2700);
  }

  async function handleBuy(item: ShopItem) {
    if (!token) return;
    if (gold < item.price) {
      showToast("Ikke råd! Du mangler gull. 💸", true);
      return;
    }

    setBuying(item.id);
    const res = await api.post<{ item: ShopItem; newGold: number }>(
      "/shop/buy",
      { itemId: item.id },
      token
    );
    setBuying(null);

    if (res.ok) {
      setGold(res.data.newGold);          // sync authoritative value from server
      setInventory((prev) => ({ ...prev, [item.id]: (prev[item.id] ?? 0) + 1 }));
      showToast(`${item.name} kjøpt! 🎉`);
    } else {
      const msg =
        res.error === "INSUFFICIENT_GOLD"
          ? "Ikke nok gull!"
          : (res.error ?? "Noe gikk galt");
      showToast(msg, true);
    }
  }

  // Group shop items by type for display
  const byType = items.reduce<Record<string, ShopItem[]>>((acc, item) => {
    (acc[item.type] ??= []).push(item);
    return acc;
  }, {});

  // Owned shop items (non-resource inventory entries)
  const ownedShopItems = items.filter((i) => (inventory[i.id] ?? 0) > 0);

  return (
    <div className="shop-page">
      {/* Header */}
      <div className="shop-page__header">
        <button className="shop-page__back" onClick={() => navigate("/map")}>
          ←
        </button>
        <span className="shop-page__title">Butikk</span>
        <div className="gold-badge">
          <span>🪙</span>
          <span>{gold}</span>
        </div>
      </div>

      {/* Body */}
      <div className="shop-page__body">
        {Object.entries(byType).map(([type, typeItems]) => (
          <section key={type}>
            <p className="shop-section-label">{TYPE_LABEL[type] ?? type}</p>
            <div className="shop-items">
              {typeItems.map((item) => {
                const canAfford = gold >= item.price;
                const owned = inventory[item.id] ?? 0;
                const isLoading = buying === item.id;

                return (
                  <div className="shop-item" key={item.id}>
                    <div className={`shop-item__icon shop-item__icon--${item.type}`}>
                      {ITEM_EMOJI[item.id] ?? "🎁"}
                    </div>
                    <div className="shop-item__info">
                      <p className="shop-item__name">{item.name}</p>
                      <p className="shop-item__desc">{item.description}</p>
                    </div>
                    <div className="shop-item__right">
                      <div className="shop-price">
                        <span>🪙</span>
                        <span>{item.price}</span>
                      </div>
                      {canAfford ? (
                        <button
                          className="btn-buy"
                          disabled={isLoading}
                          onClick={() => handleBuy(item)}
                        >
                          {isLoading ? "…" : owned > 0 ? `Kjøp igjen (${owned})` : "Kjøp"}
                        </button>
                      ) : (
                        <>
                          <button className="btn-buy" disabled>
                            Kjøp
                          </button>
                          <span className="shop-item__broke">Ikke råd</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {/* Owned items */}
        {ownedShopItems.length > 0 && (
          <section>
            <p className="shop-section-label">Ditt inventar</p>
            <div className="inventory-grid">
              {ownedShopItems.map((item) => (
                <div className="inv-card" key={item.id}>
                  <span className="inv-card__icon">{ITEM_EMOJI[item.id] ?? "🎁"}</span>
                  <span className="inv-card__name">{item.name}</span>
                  <span className="inv-card__qty">×{inventory[item.id]}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {toast && <Toast msg={toast.msg} error={toast.error} />}
    </div>
  );
}
